(function () {
  if (customElements.get('hf-marquee')) return;

  class HfMarquee extends HTMLElement {
  constructor() {
    super();

    this.wrapper = null;
    this.content = null;
    this.baseItem = null;
    this.resizeObserver = null;
    this.pendingFrame = null;
  }

  connectedCallback() {
    const run = () => {
      this._initIdle = null;
      this._initTimer = null;
      if (!this.isConnected || this._initialized) return;
      this._initialized = true;
      this.#init();
      window.addEventListener('resize', this.#handleResize);
      document.addEventListener('shopify:block:load', this.#onShopifyLoad);
      document.addEventListener('shopify:section:load', this.#onShopifyLoad);
    };
    if (typeof requestIdleCallback === 'function') {
      this._initIdle = requestIdleCallback(run, { timeout: 1500 });
    } else {
      this._initTimer = setTimeout(run, 0);
    }
  }

  disconnectedCallback() {
    if (this._initIdle != null && typeof cancelIdleCallback === 'function') {
      cancelIdleCallback(this._initIdle);
      this._initIdle = null;
    }
    if (this._initTimer != null) {
      clearTimeout(this._initTimer);
      this._initTimer = null;
    }
    window.removeEventListener('resize', this.#handleResize);
    document.removeEventListener('shopify:block:load', this.#onShopifyLoad);
    document.removeEventListener('shopify:section:load', this.#onShopifyLoad);
    this.#disconnectResizeObserver();
    if (this.pendingFrame) cancelAnimationFrame(this.pendingFrame);
  }

  #init() {
    this.wrapper = this.querySelector('[data-ref="wrapper"]');
    this.content = this.querySelector('[data-ref="content"]');

    if (!this.wrapper || !this.content) return;
    if (this.content.firstElementChild?.children.length === 0) return;

    this.#resetContent();
    this.#addRepeatedItems();
    this.#duplicateContent();
    this.#setSpeed();
    this.#restartAnimation();
    this.#observeBaseItem();
  }

  #observeBaseItem() {
    this.#disconnectResizeObserver();
    this.baseItem = this.content.firstElementChild;
    if (!this.baseItem) return;
    let firstCallback = true;
    this.resizeObserver = new ResizeObserver(() => {
      if (firstCallback) {
        firstCallback = false;
        return;
      }
      if (this.pendingFrame) cancelAnimationFrame(this.pendingFrame);
      this.pendingFrame = requestAnimationFrame(() => {
        this.pendingFrame = null;
        this.#init();
      });
    });
    this.resizeObserver.observe(this.baseItem);
  }

  #disconnectResizeObserver() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  #resetContent() {
    this.clonedContent?.remove();
    while (this.content.children.length > 1) {
      this.content.lastElementChild.remove();
    }
  }

  #onShopifyLoad = (e) => {
    const target = e.target;
    if (!(target instanceof HTMLElement)) return;
    if (target !== this && !this.contains(target) && !target.contains(this)) return;
    if (this.pendingFrame) cancelAnimationFrame(this.pendingFrame);
    this.pendingFrame = requestAnimationFrame(() => {
      this.pendingFrame = null;
      this.#init();
    });
  };

  get clonedContent() {
    const lastChild = this.wrapper.lastElementChild;
    return this.content !== lastChild ? lastChild : null;
  }

  #setSpeed(value = this.#calculateSpeed()) {
    this.style.setProperty('--hf-marquee-speed', `${value}s`);
  }

  #calculateSpeed() {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const factor = Number(this.getAttribute(isMobile ? 'data-speed-mobile' : 'data-speed-desktop')) || 30;
    const marqueeWidth = this.offsetWidth;
    const speed = Math.ceil(marqueeWidth / factor / 2);
    const mobileFactor = isMobile ? 1.25 : 1;
    return Math.max(1, Math.ceil(speed * mobileFactor));
  }

  #largeurConnue = window.innerWidth;

  #handleResize = this.debounce(() => {
    if (window.innerWidth === this.#largeurConnue) return;
    this.#largeurConnue = window.innerWidth;

    if (this.pendingFrame) cancelAnimationFrame(this.pendingFrame);
    this.pendingFrame = requestAnimationFrame(() => {
      this.pendingFrame = null;
      this.#init();
    });
  }, 250);

  #restartAnimation() {
    const animations = this.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #announceClones(root) {
    document.dispatchEvent(
      new CustomEvent('hf:content-cloned', { detail: { root: root } })
    );
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = this.content.cloneNode(true);

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('data-ref');

    this.wrapper.appendChild(clone);
    this.#announceClones(clone);
  }

  #addRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const wrapper = this.content.firstElementChild;

    if (!wrapper) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = wrapper.cloneNode(true);
      this.content.appendChild(clone);
      this.#announceClones(clone);
    }
  }

  #calculateNumberOfCopies() {
    const marqueeWidth = this.offsetWidth;
    const base = this.content.firstElementChild;
    const baseWidth = base instanceof HTMLElement ? base.offsetWidth : 1;

    if (baseWidth === 0) return 1;
    return Math.max(1, Math.ceil(marqueeWidth / baseWidth) + 1);
  }

  debounce(fn, wait) {
    let timeout;

    const debounced = (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };

    debounced.cancel = () => clearTimeout(timeout);

    return debounced;
  }
}

  customElements.define('hf-marquee', HfMarquee);
})();